// @ts-check

import { api } from '@/api'
import { getProducts } from '@/api/products'
import { getVatClasses } from '@/api/utils'
import { OWNER_TYPE_COMPANY, OWNER_TYPE_SYSTEM } from '@/core/constants'
import {
  paginationGetters,
  paginationMutations,
  paginationState
} from '@/store/paginationHelpers'
import { queryGetters, queryMutations, queryState } from '@/store/queryHelpers'
import invariant from 'invariant'

/** @returns {ProductModuleState} */
export const initProductState = () => ({
  products: null,
  vat_classes: null,
  ...paginationState,
  ...queryState
})

/** @type {ProductMutationTree} */
export const productMutations = {
  ...paginationMutations,
  ...queryMutations,
  addProducts: (state, newProducts) => {
    /** @type {Product[]} */
    let products = []

    if (Array.isArray(state.products)) {
      products = state.products
    }

    products = products.concat(newProducts)

    state.products = products
  },
  addProduct: (state, product) => {
    /** @type {Product[]} */
    let products = []
    if (Array.isArray(state.products)) {
      products = state.products
    }
    products.push(product)
    state.products = products
  },
  removeProduct: (state, id) => {
    const { products } = state

    invariant(products, 'No products')

    for (let i = 0; i < products.length; i += 1) {
      if (products[i].product.id === id) {
        products.splice(i, 1)
      }
    }
    state.products = products
  },
  removeProducts: (state) => {
    state.products = null
  },
  updateProduct: (state, data) => {
    const { products } = state

    invariant(products, 'No products')

    for (let i = 0; i < products.length; i += 1) {
      if (products[i].product.id === data.product.id) {
        products.splice(i, 1, data)
      }
    }
    state.products = products
  },
  setVatClasses: (state, vatClasses) => {
    state.vat_classes = vatClasses
  }
}

/** @type {ProductGetterTree} */
export const productGetters = {
  ...paginationGetters,
  ...queryGetters,
  loadingProducts: (state) => !state.products,
  getProduct: (state, getters) => (productId) => {
    const baseProduct = getters.products.find((product) => {
      return product.product.id === productId
    })

    if (!baseProduct) {
      return undefined
    }

    const copy = JSON.parse(JSON.stringify(baseProduct))

    // the added .vat property is only used in invoicing
    // @TODO: remove this code and find another way to display the vat data in invoicing
    const vatClass = state.vat_classes?.find((vat) => {
      return vat.id === copy.product.vat_class_id
    })

    let vat = 0

    if (vatClass) {
      vat = vatClass.percentage
    }

    copy.product.vat = vat
    return copy
  },
  productsAreFetched: (state) => {
    return state.products !== null
  },
  vatClassesAreFetched: (state) => {
    return state.vat_classes !== null
  },
  products: (state) => {
    if (!state.products) {
      return []
    }

    return state.products
  },
  getProductsWithNamesOnly: (state, getters) => {
    if (getters.productsAreFetched) {
      return state.products?.map((product) => {
        return {
          label: product.product.name ?? '',
          value: product.product.id
        }
      })
    }
    return []
  }
}

/** @type {ProductActionTree} */
export const productActions = {
  init({ dispatch }) {
    dispatch('getProducts', { reset: true })
    dispatch('fetchVatClasses')
  },
  async deleteProduct({ commit }, id) {
    await api.delete(`products/${id}`)
    commit('removeProduct', id)
  },
  getProducts(
    { commit, getters },
    {
      page = undefined,
      reset = false,
      owner = [OWNER_TYPE_SYSTEM, OWNER_TYPE_COMPANY]
    } = {}
  ) {
    if (reset) {
      commit('removeProducts')
    }

    return (
      getProducts(getters.params, page, owner)
        // @ts-expect-error - API response type doesn't match expected structure
        .then(({ data, meta }) => {
          commit('addProducts', data)
          commit('setPaginationData', meta)
        })
        .catch((e) => {
          console.error(e)
        })
    )
  },
  getNextPageOfProducts({ dispatch, getters }) {
    return dispatch('getProducts', { page: getters.currentPage + 1 })
  },
  async fetchVatClasses({ commit }) {
    const classes = await getVatClasses()
    commit('setVatClasses', classes)
  }
}

export const productModule = {
  namespaced: true,
  state: initProductState,
  actions: productActions,
  mutations: productMutations,
  getters: productGetters
}

export default productModule
